import Button from '@components/buttons/button/button.component'
import Selector from '@components/inputs/selector/selector.component'
import Loader from '@components/loader/loader.component'
import Modal from '@components/modals/modal/modal.component'
import React, { LegacyRef, useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './modal-acceptance-contract.component.scoped.scss'
import './modal-acceptance-contract.component.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  modalTitle: string
  openModal: boolean
  contract: string
  acceptTerms?: Array<string>
  loading?: boolean
  loadingSubmit?: boolean
  contractAccepted?: () => any
  contractRefused?: () => any
  closeModal?: () => any
}

export default ({
  modalTitle,
  openModal,
  contract,
  acceptTerms = [],
  loading,
  loadingSubmit,
  contractAccepted,
  contractRefused,
  closeModal,
}: Props) => {
  const [t] = useTranslation('modalAcceptanceContract')
  const [width, setWidth] = useState(window?.innerWidth)
  const [height, setHeight] = useState(window?.innerHeight)
  const [offsetHeight, setOffsetHeight] = useState(0)
  const [scrollHeight, setScrollHeight] = useState(0)
  const [scrollPositon, setScrollPositon] = useState(0)
  const [state, setState] = useState({})

  const divAcceptanceContract: LegacyRef<HTMLDivElement> = React.createRef()

  useLayoutEffect(() => {
    const updateWidth = () => {
      setWidth(window?.innerWidth)
    }
    const updateHeight = () => {
      setHeight(window?.innerHeight)
    }
    window.addEventListener('resize', updateWidth)
    window.addEventListener('resize', updateHeight)
    return () => {
      window.removeEventListener('resize', updateWidth)
      window.removeEventListener('resize', updateHeight)
    }
  }, [])

  useEffect(() => {
    let elementRef = null

    if (divAcceptanceContract?.current) {
      elementRef = divAcceptanceContract?.current
      setScrollHeight(elementRef.scrollHeight)
      setOffsetHeight(elementRef.offsetHeight)
    }

    const getScrollPosition = () => {
      if (elementRef) {
        setScrollPositon(elementRef.scrollTop)
      }
    }

    elementRef?.addEventListener('scroll', getScrollPosition, { passive: true })

    return () => {
      elementRef?.removeEventListener('scroll', getScrollPosition)
    }
  }, [divAcceptanceContract])

  useEffect(() => {
    if (acceptTerms?.length) {
      for (const term of acceptTerms) {
        setState((prevState) => ({ ...prevState, [term]: !prevState[term] ? false : true }))
      }
    }
  }, [acceptTerms])

  const parseContract = (contract) => {
    return (contract = contract?.replace(/<style\b[^<>]*>[\s\S]*?<\/style\s*>/gi, '') || '')
  }

  const acceptContract = useCallback(() => {
    if (contractAccepted && typeof contractAccepted === 'function') {
      contractAccepted()
    }
  }, [contractAccepted])

  const refuseContract = useCallback(() => {
    if (contractRefused && typeof contractRefused === 'function') {
      contractRefused()
    }
  }, [contractRefused])

  const handleChange = useCallback(
    (field: string, value: boolean) => {
      setState({ ...state, [field]: value })
    },
    [state]
  )

  const scrollBottom = useCallback(() => {
    if (divAcceptanceContract?.current) {
      divAcceptanceContract.current.scrollTo({
        top: scrollPositon + (divAcceptanceContract.current.offsetHeight - 100),
        behavior: 'smooth',
      })
    }
  }, [divAcceptanceContract, scrollPositon])

  return (
    <Modal
      className="modal-acceptance-contract"
      open={openModal}
      onCloseChange={() => closeModal()}
      buttonClose={!loadingSubmit}
      header={<h5>{modalTitle}</h5>}
      footer={
        acceptTerms?.length ? (
          <div className="display-flex justify-content-space-between" style={{ width: '100%' }}>
            <Button
              id="modal-acceptance-contract-refuse"
              onClick={() => refuseContract()}
              disabled={loadingSubmit}
              size="sm"
              color="transparent"
              textColor="var(--gray)"
              borderColor="var(--gray)"
            >
              <span>{t('refuse')}</span>
            </Button>
            <Button
              id="modal-acceptance-contract-accept"
              onClick={() => acceptContract()}
              loading={loadingSubmit}
              disabled={!Object.values(state).every((fieldValue) => !!fieldValue)}
              size="sm"
            >
              <span>{t('accept')}</span>
            </Button>
          </div>
        ) : (
          <Button id="modal-acceptance-contract-close" onClick={() => closeModal()}>
            Fechar
          </Button>
        )
      }
      modalSize="lg"
    >
      {loading ? (
        <div className="display-flex justify-content-center py-3">
          <Loader size="big" />
        </div>
      ) : (
        <>
          <div
            className="content-contract-modal acceptance-contract scrollbar"
            ref={divAcceptanceContract}
            style={{
              height:
                width < 992
                  ? `calc(${height}px - 54px - 62px ${scrollPositon < scrollHeight - offsetHeight ? '- 50px' : ''})`
                  : `calc(70vh ${scrollPositon < scrollHeight - offsetHeight ? '' : '+ 50px'}`,
            }}
          >
            <div className="contract-styled" dangerouslySetInnerHTML={{ __html: parseContract(contract) }} />
            {acceptTerms?.length ? (
              <>
                <hr />
                <div className="accept-terms">
                  {acceptTerms?.map((term, index) => (
                    <Selector
                      key={`${index}-${term}`}
                      id={`${index}-${term}`}
                      name={`${index}-${term}`}
                      checked={state?.[term]}
                      onChange={() => handleChange(term, !state?.[term])}
                      errorSpan={false}
                      type="checkbox"
                      rightLabel={<p dangerouslySetInnerHTML={{ __html: term }} />}
                    />
                  ))}
                </div>
              </>
            ) : null}
            {scrollPositon < scrollHeight - offsetHeight ? <div className="gradient-blur"></div> : null}
          </div>
          {scrollPositon < scrollHeight - offsetHeight ? (
            <div className="read-more" role="button" tabIndex={0} onClick={() => scrollBottom()}>
              <i className="fa-solid fa-angle-down"></i>
              {t('readMore')}
            </div>
          ) : null}
        </>
      )}
    </Modal>
  )
}
